import { apiFetch, setCsrfToken } from '../client'
import { changePasswordResponseSchema, profilePatchResponseSchema, sessionResponseSchema } from '../schemas'
import type { SessionResponseParsed } from '../schemas'
import type { AvatarColorName } from '@/lib/avatarColors'

/** Auth calls. */

export async function login(email: string, password: string): Promise<SessionResponseParsed> {
  const raw = await apiFetch<unknown>('/auth/login', {
    method: 'POST',
    body: { email, password },
  })
  const parsed = sessionResponseSchema.parse(raw)
  setCsrfToken(parsed.csrf_token)
  return parsed
}

export async function me(signal?: AbortSignal): Promise<SessionResponseParsed> {
  const raw = await apiFetch<unknown>('/auth/me', { signal })
  const parsed = sessionResponseSchema.parse(raw)
  setCsrfToken(parsed.csrf_token)
  return parsed
}

export async function logout(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { method: 'POST' })
  } finally {
    // Clear the token whatever happened. A failed logout must not leave.
    setCsrfToken(null)
  }
}

/** Changing a password rotates the session id, and the CSRF token rotates with */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const raw = await apiFetch<unknown>('/auth/change-password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  })
  setCsrfToken(changePasswordResponseSchema.parse(raw).csrf_token)
}

/**
 * Profile updates (name and avatar). Email and role are admin-only.
 */
export async function updateProfile(body: {
  name?: string
  avatar_color?: AvatarColorName
}): Promise<SessionResponseParsed['user']> {
  const raw = await apiFetch<unknown>('/auth/profile', { method: 'PATCH', body })
  return profilePatchResponseSchema.parse(raw).user
}
